import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import useLegalAgreementsApi from '../../../../hooks/useLegalAgreementsApi'
import { useAuth } from '../../../../lib/auth'
import { userClaimIds } from '../../../../utils/userClaims'
import { UserDataProviderContext } from '../../clickwrap/context'
import { UserDataFormComponentProps, UserDataProviderContextValue } from '../../clickwrap/types'
import { CoveredEntityFormFieldValues } from '../covered-entity/types'
import CoveredEntityForm, { CoveredEntityFormProps } from './CoveredEntityForm'

function formValueFromBoolean(value: boolean | undefined | null): 'yes' | 'no' | '' {
  if (value === true) {
    return 'yes'
  }
  if (value === false) {
    return 'no'
  }
  return ''
}

const curryCoveredEntityForm = (
  hocProps: Pick<CoveredEntityFormProps, 'control' | 'errors' | 'watch'>,
): React.FC<UserDataFormComponentProps> =>
  function CurriedCoveredEntityForm(props) {
    return <CoveredEntityForm {...hocProps} {...props} />
  }

const CoveredEntityContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const isMounted = useRef(true)
  const [editRequested, setEditRequested] = useState(false)
  const [userCoveredEntity, setUserCoveredEntity] = useState<boolean | null>(null)
  const [isActive, setIsActive] = useState(false)
  const {
    error: apiError,
    loading: apiLoading,
    onCoveredEntityUpdatedAsync,
  } = useLegalAgreementsApi()

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setUserCoveredEntity(null)
      return
    }
    const claimValue = user[userClaimIds.hipaaCoveredEntity]
    if (typeof claimValue !== 'boolean') {
      setUserCoveredEntity(null)
      return
    }
    setUserCoveredEntity(claimValue)
  }, [user])

  const methods = useForm<CoveredEntityFormFieldValues>({
    defaultValues: {
      hipaaCoveredEntity: formValueFromBoolean(userCoveredEntity),
    },
    mode: 'onChange',
    // prevent form from losing values on re-render
    shouldUnregister: false,
  })

  const hipaaCoveredEntityValue = methods.watch('hipaaCoveredEntity')

  // reset form to update default value when not active since userCoveredEntity is determined after first render
  useEffect(() => {
    const formValue = formValueFromBoolean(userCoveredEntity)
    if (!isActive && hipaaCoveredEntityValue !== formValue) {
      methods.reset({
        hipaaCoveredEntity: formValue,
      })
    }
  }, [hipaaCoveredEntityValue, isActive, methods, userCoveredEntity])

  const onSubmit: SubmitHandler<CoveredEntityFormFieldValues> = useCallback(
    async (data) => {
      const { hipaaCoveredEntity } = data
      const newValue = hipaaCoveredEntity === 'yes'

      const saveIfChangedPromise: Promise<unknown> =
        userCoveredEntity === newValue
          ? Promise.resolve()
          : onCoveredEntityUpdatedAsync({ hipaaCoveredEntity: newValue })

      return saveIfChangedPromise
        .then(() => {
          if (isMounted.current) {
            // reset editRequested on success
            setEditRequested(false)
          }
        })
        .catch((err) => {
          console.error(err)
        })
    },
    [onCoveredEntityUpdatedAsync, userCoveredEntity],
  )

  // memoize curried form component to prevent unnecessary re-renders
  const FormComponent = useMemo(
    () =>
      curryCoveredEntityForm({
        control: methods.control,
        errors: methods.errors,
        watch: methods.watch,
      }),
    [methods.control, methods.errors, methods.watch],
  )

  useEffect(() => {
    if (editRequested || methods.formState.isSubmitting || userCoveredEntity == null) {
      setIsActive(true)
    } else {
      setIsActive(false)
    }
  }, [editRequested, methods.formState.isSubmitting, userCoveredEntity])

  const contextValue: UserDataProviderContextValue = {
    error: apiError,
    isActive,
    isFormValid: methods.formState.isValid,
    isSubmitting: methods.formState.isSubmitting || apiLoading,
    onSubmit: methods.handleSubmit(onSubmit),
    editData() {
      methods.reset({
        hipaaCoveredEntity: formValueFromBoolean(userCoveredEntity),
      })
      setEditRequested(true)
    },
    FormComponent,
  }

  return (
    <UserDataProviderContext.Provider value={contextValue}>
      {children}
    </UserDataProviderContext.Provider>
  )
}

export default CoveredEntityContextProvider
